import type { NavigateFunction } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCampaign } from '@/hooks/useCampaign';
import { getPathForPage } from '@/routes';

export interface CampaignReportEmbedPageProps {
  campaignId: string | undefined;
  navigate: NavigateFunction;
}

/**
 * Full-viewport iframe for the campaign Looker report URL (same shell sizing as DiscoveryEmbedPage).
 */
export function CampaignReportEmbedPage({ campaignId, navigate }: CampaignReportEmbedPageProps) {
  const { t } = useTranslation();
  const {
    data: campaignDetail,
    isLoading,
    isError,
  } = useCampaign({ campaignId });

  const reportUrl = (campaignDetail?.report_url ?? '').trim();
  const summaryPath =
    campaignId != null && campaignId.length > 0
      ? getPathForPage('see-campaigns', { campaignId, subTab: 'summary' })
      : '/campaigns';

  const backBar = (
    <div className="shrink-0 px-4 py-2 border-b border-gray-100 bg-[var(--background)]">
      <button
        type="button"
        onClick={() => navigate(summaryPath)}
        className="inline-flex items-center gap-2 text-[13px] text-[#667085] hover:text-[#101828] transition-colors"
        style={{ fontWeight: 600 }}
      >
        <ArrowLeft className="w-4 h-4" />
        {t('campaigns.reportEmbedBackToResults')}
      </button>
    </div>
  );

  if (isLoading && !campaignDetail) {
    return (
      <div className="flex min-h-[calc(100vh-3.5rem)] flex-col bg-[var(--background)]">
        {backBar}
        <div className="flex flex-1 items-center justify-center p-6" role="status" aria-live="polite">
          <Loader2 className="w-8 h-8 text-[#be43ff] animate-spin" aria-hidden />
          <span className="sr-only">{t('campaigns.loadingResultsSr')}</span>
        </div>
      </div>
    );
  }

  if (isError || !reportUrl) {
    return (
      <div className="flex min-h-[calc(100vh-3.5rem)] flex-col bg-[var(--background)]">
        {backBar}
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="text-sm text-muted-foreground max-w-md" role="alert">
            {t('campaigns.reportEmbedUnavailable')}
          </p>
          <button
            type="button"
            onClick={() => navigate(summaryPath)}
            className="inline-flex items-center gap-2 rounded-xl bg-[#be43ff] px-5 py-2.5 text-[13px] text-white hover:bg-[#a830e0] transition-colors"
            style={{ fontWeight: 600 }}
          >
            {t('campaigns.reportEmbedBackToResults')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] min-h-[calc(100vh-3.5rem)] w-full flex-col bg-[var(--background)]">
      {backBar}
      <div className="flex min-h-0 flex-1 flex-col">
        <iframe
          key={reportUrl}
          src={reportUrl}
          className="h-full w-full min-h-0 border-0"
          title={t('campaigns.reportEmbedTitle')}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );
}
